import * as FileSaver from 'file-saver';

import { ChatMessage } from '../../types/Chatbot';

export interface ChatExportLabels {
  title: string;
  user: string;
  assistant: string;
  locale: string;
}

export function buildChatMarkdown(messages: ChatMessage[], labels: ChatExportLabels): string {
  const sections = messages.map((message) => {
    const author = message.role === 'user' ? labels.user : labels.assistant;
    return `### ${author} · ${message.timestamp.toLocaleString(labels.locale)}\n\n${message.content.trim()}`;
  });
  return [`# ${labels.title}`, ...sections].join('\n\n---\n\n') + '\n';
}

export function downloadChatMarkdown(messages: ChatMessage[], labels: ChatExportLabels): void {
  const blob = new Blob([buildChatMarkdown(messages, labels)], { type: 'text/markdown;charset=utf-8' });
  FileSaver.saveAs(blob, `${exportFileName()}.md`);
}

/** Prints the chat as rendered on screen; the browser's print dialog offers "Save as PDF". */
export function printChat(messagesElement: HTMLElement, title: string): void {
  const iframe = document.createElement('iframe');
  // No scripts run inside; allow-modals is needed for print().
  iframe.sandbox.add('allow-modals', 'allow-same-origin');
  iframe.style.cssText = 'position: fixed; width: 0; height: 0; border: 0;';
  iframe.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><title>${exportFileName()}</title>
    <style>${PRINT_STYLE}</style></head>
    <body><h1></h1>${messagesElement.innerHTML}</body></html>`;
  iframe.onload = (): void => {
    const printWindow = iframe.contentWindow!;
    printWindow.document.querySelector('h1')!.textContent = title;
    printWindow.addEventListener('afterprint', () => iframe.remove());
    printWindow.print();
  };
  document.body.appendChild(iframe);
}

function exportFileName(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `vev-iq-chat-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
}

const PRINT_STYLE = `
  body { font-family: Arial, sans-serif; font-size: 11pt; color: #262626; display: flex; flex-direction: column; }
  h1 { font-size: 16pt; }
  button, mat-icon, .warning-message, .error-message, .streaming-cursor { display: none; }
  .chat-message { max-width: 85%; margin-bottom: 12px; break-inside: avoid; }
  .chat-message.user { align-self: flex-end; }
  .chat-message.assistant:has(table) { max-width: 100%; }
  .message-content { padding: 8px 12px; border-radius: 8px; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  .user .message-content { background: #d4f5d0; }
  .assistant .message-content { background: #f5f5f5; }
  .message-content p { margin: 0 0 6px; }
  .message-time { font-size: 8pt; color: #737373; margin-top: 2px; }
  .user .message-time { text-align: right; }
  pre { background: #262626; color: #fafafa; padding: 8px; border-radius: 6px; white-space: pre-wrap; }
  code { font-family: Consolas, Monaco, monospace; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #d4d4d4; padding: 4px 8px; text-align: left; }
  th { background: #e5e5e5; }
`;
